import { useState, FormEvent } from "react";
import { Link } from "react-router-dom";

interface FinalCTAProps {
  title?: string;
  subtitle?: string;
}

export default function FinalCTA({ title, subtitle }: FinalCTAProps) {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const message = (form.elements.namedItem("message") as HTMLTextAreaElement)?.value ?? "";
    if (message.length > 500) return;

    setSubmitting(true);
    try {
      const formData = new URLSearchParams();
      const fields = ["name", "phone", "email", "project_type", "message"];
      fields.forEach((f) => {
        const el = form.elements.namedItem(f) as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
        if (el) formData.append(f, el.value);
      });

      await fetch("https://readdy.ai/api/form/d6uk0kedp0k8rarn7log", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: formData.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitting(false);
    }
  };

  return (
    <section id="estimate" className="py-24 bg-[#141414]">
      <div className="max-w-7xl mx-auto px-6">
        {/* Urgency header */}
        <div className="text-center mb-4">
          <div className="inline-flex items-center gap-2 bg-brand-amber/20 border border-brand-amber/40 rounded-full px-4 py-2 mb-6">
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-alarm-warning-line text-brand-amber text-sm" />
            </span>
            <span className="text-brand-amber text-xs font-body font-semibold">
              Limited Project Slots Available — Book Your Free Estimate Today
            </span>
          </div>
          <h2 className="font-heading font-bold text-4xl lg:text-5xl text-white leading-tight mb-4">
            {title ?? "Start Your Construction Project Today"}
          </h2>
          <p className="font-body text-white/60 text-base max-w-xl mx-auto">
            {subtitle ??
              "Ready to build something great? Get a free, no-obligation estimate. Our team responds within 24 hours."}
          </p>
        </div>

        {/* Quick action buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-14">
          <a
            href="tel:+17195550100"
            className="inline-flex items-center justify-center gap-2 bg-white text-brand-dark px-7 py-3.5 rounded-full font-body font-semibold text-sm hover:bg-brand-warm transition-colors cursor-pointer whitespace-nowrap"
          >
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-phone-fill text-brand-amber text-sm" />
            </span>
            Call (719) 555-0100
          </a>
          <Link
            to="#estimate"
            className="inline-flex items-center justify-center gap-2 border border-white/30 text-white px-7 py-3.5 rounded-full font-body font-semibold text-sm hover:border-white transition-colors cursor-pointer whitespace-nowrap"
          >
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-mail-line text-sm" />
            </span>
            Submit the Form Below
          </Link>
        </div>

        {/* Form + contact info grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Form */}
          <div className="lg:col-span-2 bg-brand-warm rounded-2xl p-8 lg:p-10">
            {submitted ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 flex items-center justify-center bg-brand-amber/10 rounded-full mx-auto mb-4">
                  <span className="w-8 h-8 flex items-center justify-center">
                    <i className="ri-check-line text-3xl text-brand-amber" />
                  </span>
                </div>
                <h3 className="font-heading font-bold text-2xl text-brand-dark mb-2">Request Received!</h3>
                <p className="font-body text-brand-charcoal text-sm">
                  Thank you for reaching out. Our team will contact you within 24 hours to discuss your project.
                </p>
              </div>
            ) : (
              <form data-readdy-form onSubmit={handleSubmit} className="flex flex-col gap-5">
                <div className="mb-2">
                  <h3 className="font-heading font-bold text-xl text-brand-dark">
                    Request a Free Estimate
                  </h3>
                  <p className="font-body text-sm text-brand-charcoal/70 mt-1">
                    Fill out the form and we&apos;ll get back to you within 24 hours.
                  </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs font-body font-semibold text-brand-dark mb-1.5 uppercase tracking-wide">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      placeholder="John Smith"
                      className="w-full px-4 py-3 bg-white border border-brand-warm-deep rounded-lg text-sm font-body text-brand-dark placeholder:text-brand-charcoal/40 focus:outline-none focus:border-brand-amber transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-body font-semibold text-brand-dark mb-1.5 uppercase tracking-wide">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      required
                      placeholder="(719) 555-0100"
                      className="w-full px-4 py-3 bg-white border border-brand-warm-deep rounded-lg text-sm font-body text-brand-dark placeholder:text-brand-charcoal/40 focus:outline-none focus:border-brand-amber transition-colors"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-body font-semibold text-brand-dark mb-1.5 uppercase tracking-wide">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    placeholder="you@example.com"
                    className="w-full px-4 py-3 bg-white border border-brand-warm-deep rounded-lg text-sm font-body text-brand-dark placeholder:text-brand-charcoal/40 focus:outline-none focus:border-brand-amber transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-xs font-body font-semibold text-brand-dark mb-1.5 uppercase tracking-wide">
                    Project Type *
                  </label>
                  <select
                    name="project_type"
                    required
                    className="w-full px-4 py-3 bg-white border border-brand-warm-deep rounded-lg text-sm font-body text-brand-dark focus:outline-none focus:border-brand-amber transition-colors cursor-pointer"
                  >
                    <option value="">Select project type...</option>
                    <option value="Custom Home Building">Custom Home Building</option>
                    <option value="Home Remodeling">Home Remodeling &amp; Renovations</option>
                    <option value="Commercial Construction">Commercial Construction</option>
                    <option value="Home Addition">Additions &amp; Expansions</option>
                    <option value="Interior Upgrades">Interior &amp; Exterior Upgrades</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-body font-semibold text-brand-dark mb-1.5 uppercase tracking-wide">
                    Project Details
                  </label>
                  <textarea
                    name="message"
                    rows={4}
                    maxLength={500}
                    placeholder="Tell us about your project, timeline, budget range, and any specific requirements..."
                    className="w-full px-4 py-3 bg-white border border-brand-warm-deep rounded-lg text-sm font-body text-brand-dark placeholder:text-brand-charcoal/40 focus:outline-none focus:border-brand-amber transition-colors resize-none"
                    onChange={(e) => setCharCount(e.target.value.length)}
                  />
                  <p className="text-xs text-brand-charcoal/50 font-body mt-1 text-right">{charCount}/500</p>
                </div>
                <button
                  type="submit"
                  disabled={submitting || charCount > 500}
                  className="w-full bg-brand-amber text-white py-4 rounded-lg font-body font-bold text-sm hover:bg-brand-amber-light transition-colors cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {submitting ? "Submitting..." : "Submit Free Estimate Request →"}
                </button>
                <p className="text-center text-xs text-brand-charcoal/50 font-body">
                  No commitment required. We respond within 24 hours.
                </p>
              </form>
            )}
          </div>

          {/* Contact info sidebar */}
          <div className="flex flex-col gap-6">
            <div className="bg-white/5 border border-white/10 rounded-2xl p-7">
              <p className="text-xs font-body font-semibold text-white/40 uppercase tracking-widest mb-5">
                Contact Us Directly
              </p>
              <div className="flex flex-col gap-5">
                <a href="tel:+17195550100" className="flex items-start gap-3 group cursor-pointer">
                  <div className="w-10 h-10 flex items-center justify-center bg-brand-amber/20 rounded-lg flex-shrink-0">
                    <i className="ri-phone-fill text-brand-amber" />
                  </div>
                  <div>
                    <p className="text-xs text-white/40 font-body mb-0.5">Phone</p>
                    <p className="text-sm font-body font-semibold text-white group-hover:text-brand-amber transition-colors">
                      (719) 555-0100
                    </p>
                  </div>
                </a>
                <a href="mailto:info@westecbuilders.com" className="flex items-start gap-3 group cursor-pointer">
                  <div className="w-10 h-10 flex items-center justify-center bg-brand-amber/20 rounded-lg flex-shrink-0">
                    <i className="ri-mail-fill text-brand-amber" />
                  </div>
                  <div>
                    <p className="text-xs text-white/40 font-body mb-0.5">Email</p>
                    <p className="text-sm font-body font-semibold text-white group-hover:text-brand-amber transition-colors break-all">
                      info@westecbuilders.com
                    </p>
                  </div>
                </a>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-brand-amber/20 rounded-lg flex-shrink-0">
                    <i className="ri-map-pin-fill text-brand-amber" />
                  </div>
                  <div>
                    <p className="text-xs text-white/40 font-body mb-0.5">Location</p>
                    <p className="text-sm font-body text-white/80">
                      Colorado Springs, CO<br />Serving the Front Range
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-brand-amber rounded-2xl p-7">
              <p className="font-heading font-bold text-xl text-white mb-2">Fast Response</p>
              <p className="font-body text-sm text-white/80 leading-relaxed mb-4">
                We respond to all estimate requests within 24 hours. Emergency construction consultations available.
              </p>
              <div className="flex flex-col gap-2">
                {["Free, no-obligation estimates", "Same-day call-back available", "Licensed & insured contractor"].map((item) => (
                  <div key={item} className="flex items-center gap-2">
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-check-line text-white/80 text-sm" />
                    </span>
                    <span className="text-xs font-body text-white/80">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
